import type { FundLocationResponse } from '@polaris/core/services'
import { runnerSessionDataAtom } from '@polaris/core/stores/mpc/atom'
import { formatCoin, formatPrice } from '@polaris/core/utils'
import { useAtomValue } from 'jotai'
import { Check, CircleAlert, X } from 'lucide-react'
import { useMpcRecoverPrivacyFunds } from '@/src/hooks/mpc/use-mpc-recover-privacy-funds'
import { useScanPrivacyFunds } from '@/src/queries/hooks/mpc'
import { routeAtom } from '@/src/stores/route-machine/atoms'
import { cn } from '@/src/utils/styles'
import { Button } from '../ui/button'
import {
  ListRow,
  ListRowImage,
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
} from '../ui/list-row'
import { Spinner } from '../ui/spinner'

interface RouteMpcDetailsProps {
  onBack?: () => void
}

export const RouteMpcDetails = ({ onBack }: RouteMpcDetailsProps) => {
  const route = useAtomValue(routeAtom)
  const mpcSessionData = useAtomValue(runnerSessionDataAtom)

  const sessionKeyData = mpcSessionData
    ? {
        walletSig: mpcSessionData.authSig,
        sessionKeyPair: mpcSessionData.sessionKeyPair,
      }
    : undefined

  const { data, isLoading, error } = useScanPrivacyFunds({
    routeId: route?.ID ?? '',
    sessionKeyData,
    enabled: !!route?.ID && !!sessionKeyData,
  })

  const recoverableFunds = data?.recoverableFunds ?? []
  const highestValueFund = data?.highestValueFund

  if (isLoading) {
    return (
      <div>
        <div className='flex items-center place-content-between text-center sm:text-left gap-5 px-5 sm:px-8 min-h-12 sm:min-h-20 z-[51]'>
          <div className='size-6' aria-hidden />
          <div className='flex flex-col items-center justify-center'>
            <span className='text-center text-xl font-semibold text-primary z-[52]'>
              Autopilot settings
            </span>
          </div>
          <Button onClick={onBack} type='button' variant='ghost'>
            <X width={24} height={24} />
            <span className='sr-only'>Close</span>
          </Button>
        </div>
        <div className='flex items-center justify-center p-8'>
          <Spinner />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <div className='flex items-center place-content-between text-center sm:text-left gap-5 px-5 sm:px-8 min-h-12 sm:min-h-20 z-[51]'>
          <div className='size-6' aria-hidden />
          <div className='flex flex-col items-center justify-center'>
            <span className='text-center text-xl font-semibold text-primary z-[52]'>
              Autopilot settings
            </span>
          </div>
          <Button onClick={onBack} type='button' variant='ghost'>
            <X width={24} height={24} />
            <span className='sr-only'>Close</span>
          </Button>
        </div>
        <div className='flex flex-col items-center justify-center p-8 text-danger-text text-center'>
          <CircleAlert height={48} width={48} className='mb-4' />
          <p>Failed to load recoverable funds</p>
          <p className='text-sm text-secondary'>{error.message}</p>
        </div>
      </div>
    )
  }

  if (!data) return null

  return (
    <div>
      <div className='flex items-center place-content-between text-center sm:text-left gap-5 px-5 sm:px-8 min-h-12 sm:min-h-20 z-[51]'>
        <div className='size-6' aria-hidden />
        <div className='flex flex-col items-center justify-center'>
          <span className='text-center text-xl font-semibold text-primary z-[52]'>
            Autopilot settings
          </span>
        </div>
        <Button onClick={onBack} type='button' variant='ghost'>
          <X width={24} height={24} />
          <span className='sr-only'>Close</span>
        </Button>
      </div>

      <div className='flex flex-col px-5 py-4 animate-fade-in'>
        {recoverableFunds.length === 0 ? (
          <div className='flex flex-col items-center justify-center p-8 text-secondary text-center'>
            <p>No recoverable funds available</p>
          </div>
        ) : (
          <>
            <p className='text-secondary text-sm pb-3 leading-6'>
              Automatic recovery failed. You can manually recover your funds
              back to the Privacy Vault. Only the highest value asset can be
              recovered at a time.
            </p>
            <div className='py-3 max-h-[400px] overflow-y-auto space-y-3'>
              {recoverableFunds.map((fund) => {
                const isHighestValue =
                  !!highestValueFund &&
                  fund.chainID === highestValueFund.chainID &&
                  fund.denom === highestValueFund.denom
                return (
                  <PrivacyFundRecoveryRow
                    key={fund.chainID + fund.denom}
                    fund={fund}
                    isHighestValue={isHighestValue}
                    routeId={route?.ID ?? ''}
                  />
                )
              })}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

interface PrivacyFundRecoveryRowProps {
  fund: FundLocationResponse
  isHighestValue: boolean
  routeId: string
}

const PrivacyFundRecoveryRow = ({
  fund,
  isHighestValue,
  routeId,
}: PrivacyFundRecoveryRowProps) => {
  const { mutate, isSuccess, isError, isPending, error } =
    useMpcRecoverPrivacyFunds()

  const ctaDisabled = !isHighestValue || isSuccess || isPending

  return (
    <div className='flex flex-col bg-surface1 rounded-xl'>
      <ListRow>
        {isSuccess ? (
          <div className='flex items-center text-success-text'>
            <div className='flex items-center justify-center w-10 h-10 bg-surface1 rounded-full mr-2'>
              <Check height={24} width={24} />
            </div>
            Sent back to Privacy Vault
          </div>
        ) : isError ? (
          <div className='flex items-center text-danger-text'>
            <div className='flex items-center justify-center w-10 h-10 bg-surface1 rounded-full mr-2'>
              <CircleAlert height={24} width={24} />
            </div>
            <div className='flex items-center gap-1'>
              <span className='flex-1'>
                {error?.message || 'Failed to recover to Privacy Vault'}
              </span>
            </div>
          </div>
        ) : (
          <>
            {fund.logo_img_url && (
              <ListRowImage
                src={fund.logo_img_url}
                alt={`${fund.ticker} icon`}
                width={48}
                height={48}
                className='rounded-full h-12 w-12'
                classes={{ container: 'flex-shrink-0' }}
                unoptimized
                chainImageAlt={fund.chainName}
                chainImageSrc={fund.chain_img}
                chainId={fund.chainID}
                placeholderName={fund.display_name}
              />
            )}
            <ListRowSide containerClassName='w-full'>
              <ListRowSideName>{fund.ticker}</ListRowSideName>
              <ListRowSideDescription>
                on {fund.chainName}
              </ListRowSideDescription>
            </ListRowSide>

            <ListRowSide className='text-right'>
              <ListRowSideName>{formatPrice(fund.usdValue)}</ListRowSideName>
              <ListRowSideDescription>
                {formatCoin({
                  amount: Number(fund.normalized_amount),
                })}
              </ListRowSideDescription>
            </ListRowSide>
          </>
        )}
      </ListRow>
      <hr className='border-surface1' />
      <div className='px-8 py-4'>
        <Button
          onClick={() => {
            mutate({
              routeId,
            })
          }}
          className={cn(
            'w-full !rounded-t-none rounded-b-xl hover:rounded-b-xl',
            {
              'opacity-50': ctaDisabled,
            },
          )}
          disabled={ctaDisabled}
        >
          {isPending ? (
            <>
              <Spinner /> Recovery in progress
            </>
          ) : !isHighestValue ? (
            'Lower value - recover highest first'
          ) : fund.isDust ? (
            'Below dust threshold'
          ) : (
            'Send back to Privacy Vault'
          )}
        </Button>
      </div>
    </div>
  )
}
